import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="documents--number-updater"
export default class extends Controller {
  // expected: the hidden forecast an issue posts back (expected_number), so the
  // server can say when the minted number differs from what the user saw.
  static targets = ["number", "numberSeriesId", "deliveryDate", "issueDate", "expected"]

  // The forecast has several writers — this controller on a date change, the
  // number-series picker, the create-series stream — and they write the number
  // field's placeholder. So expected_number is copied from that placeholder at
  // the moment the form submits, not kept in step by each writer: a writer that
  // forgot it would make the server blame "another document" for a number the
  // user simply switched away from. Bound to the form's own submit, which runs
  // before Turbo (it starts the submission when the event bubbles to document).
  connect() {
    this.form = this.element.closest("form")
    this.syncExpected = this.syncExpected.bind(this)
    this.form?.addEventListener("submit", this.syncExpected)
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.syncExpected)
  }

  syncExpected() {
    if (!this.hasExpectedTarget || !this.hasNumberTarget) return
    this.expectedTarget.value = this.numberTarget.placeholder
  }

  updateNumber() {
    if (!this.hasNumberSeriesIdTarget || !this.hasNumberTarget) return

    const workspaceId = document.body.getAttribute("data-params-workspace-id")
    const numberSeriesId = this.numberSeriesIdTarget.value
    if (!numberSeriesId) return
    const baseUrl = window.location.origin

    const deliveryDate = this.hasDeliveryDateTarget ? this.deliveryDateTarget.value : null
    const issueDate = this.hasIssueDateTarget ? this.issueDateTarget.value : null
    // The issue date first: the number is minted for the issue date
    // (Lifecycle::Machine, Document#issue_date), and the server renders the
    // builder's preview for it too. Forecasting from the delivery date showed
    // FAK2026-… for an invoice delivered in December and issued in January.
    const date = issueDate || deliveryDate

    const isNumberlessDraft = this.numberTarget.dataset.numberSeriesDraftPreview === "true"
    const url = `${baseUrl}/${workspaceId}/number_series_picker/${numberSeriesId}?date=${date || ""}&draft=${isNumberlessDraft}`

    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => {
        if (!r.ok) {
          return r.json().then((data) => Promise.reject(new Error(data.error || r.statusText)))
        }
        return r.json()
      })
      .then((data) => {
        if (data.number != null) {
          if (isNumberlessDraft) {
            this.numberTarget.placeholder = data.number
            return
          }

          // A document that already carries a number keeps it. Rewriting a
          // filled field on a date change is how a draft that showed
          // OF20260019 got issued as OF20260020 (prod, 2026-09-06): the
          // fetched preview is the series' NEXT number, not this document's.
          if (this.numberTarget.value.trim() !== "") return

          this.numberTarget.value = data.number
          this.numberTarget.dispatchEvent(new Event("input", { bubbles: true }))
        }
      })
      .catch((error) => {
        console.error("Failed to fetch document number:", error)
      })
  }
}
